import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { runNodeAnalysis, getNodeAnalyses } from "../../actions/aiAction";
import "./NodeAnalysis.css";

const PARAMS = [
  { key: "consistency", label: "Consistency",  hint: "How regularly you show up" },
  { key: "depth",       label: "Depth",        hint: "How specific and real your updates are" },
  { key: "velocity",    label: "Progress",     hint: "How much has actually changed" },
  { key: "alignment",   label: "On Track",     hint: "Are updates about your actual intention" },
  { key: "recency",     label: "Recency",      hint: "How long since your last update" },
];

const TIPS = [
  "Reading your update history...",
  "Looking at how much has changed over time...",
  "Checking how regularly you've shown up...",
  "Measuring the depth of your entries...",
  "Seeing if you've stayed on track...",
  "Putting together your report...",
];

const color = (n) => n >= 70 ? "#22c55e" : n >= 40 ? "#ca8a04" : "#dc2626";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

/* ── Score ring ─────────────────────────────────────────────── */

const ScoreRing = ({ value, size = 140, stroke = 6 }) => {
  const r      = (size - stroke) / 2;
  const c      = 2 * Math.PI * r;
  const pct    = Math.max(0, Math.min(value ?? 0, 100));
  const offset = c - (pct / 100) * c;
  const clr    = color(pct);

  return (
    <div className="na-ring-wrap" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#1a1a1a" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={clr} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="na-ring-progress" />
      </svg>
      <div className="na-ring-inner">
        <span className="na-ring-num" style={{ color: clr }}>{value ?? "—"}</span>
      </div>
    </div>
  );
};

/* ── Tip loader ─────────────────────────────────────────────── */

const TipLoader = () => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % TIPS.length);
        setFade(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="na-loading">
      <div className="na-loading-bar">
        <div className="na-loading-bar-fill" />
      </div>
      <span className={`na-loading-tip ${fade ? "visible" : ""}`}>{TIPS[idx]}</span>
    </div>
  );
};

/* ── Report ─────────────────────────────────────────────────── */

const Report = ({ analysis, deltas, previousAnalyzedAt, isHistory }) => {
  const scores    = analysis.scores || {};
  const report    = analysis.report || {};
  const composite = scores.composite ?? null;
  const compClr   = color(composite);

  return (
    <div className={`na-report ${isHistory ? "na-report-history" : ""}`}>

      {/* SCORE */}
      <div className="na-comp">
        <ScoreRing value={composite} size={isHistory ? 100 : 140} stroke={isHistory ? 5 : 6} />
        <div className="na-comp-side">
          <span className="na-comp-label">Score</span>
          <span className="na-comp-big" style={{ color: compClr }}>
            {composite ?? "—"}<span className="na-comp-of">/100</span>
          </span>
          {deltas?.composite != null && (
            <span className={`na-comp-delta ${deltas.composite > 0 ? "up" : deltas.composite < 0 ? "down" : "flat"}`}>
              {deltas.composite > 0 ? `+${deltas.composite}` : deltas.composite} pts from last
            </span>
          )}
          {previousAnalyzedAt && <span className="na-comp-prev">vs. {fmt(previousAnalyzedAt)}</span>}
        </div>
      </div>

      {/* PARAMS */}
      <div className="na-params">
        {PARAMS.map(({ key, label, hint }) => {
          const s   = scores[key] ?? null;
          const pct = Math.max(0, Math.min(s ?? 0, 100));
          const d   = deltas?.[key] ?? null;
          const clr = color(pct);
          return (
            <div key={key} className="na-p-row">
              <div className="na-p-label-wrap">
                <span className="na-p-label">{label}</span>
                <span className="na-p-hint">{hint}</span>
              </div>
              <div className="na-p-bar-wrap">
                <div className="na-p-bar">
                  <div className="na-p-fill" style={{ width: `${pct}%`, background: clr }} />
                </div>
              </div>
              <span className="na-p-score" style={{ color: clr }}>{s ?? "—"}</span>
              {d != null && (
                <span className={`na-p-delta ${d > 0 ? "up" : d < 0 ? "down" : "flat"}`}>
                  {d > 0 ? `+${d}` : d}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* STRENGTHS */}
      {report.strengths?.length > 0 && (
        <div className="na-block">
          <h3 className="na-block-heading na-h-green">What you're doing well</h3>
          {report.strengths.map((s, i) => (
            <p key={i} className="na-block-item">
              <span className="na-bullet green" />
              {s}
            </p>
          ))}
        </div>
      )}

      {/* WHERE YOU ARE */}
      {report.progressRead && (
        <div className="na-block">
          <h3 className="na-block-heading">Where you are</h3>
          <p className="na-block-body">{report.progressRead}</p>
        </div>
      )}

      {/* WHERE TO FOCUS */}
      {report.improvements?.length > 0 && (
        <div className="na-block">
          <h3 className="na-block-heading na-h-amber">Where to focus</h3>
          {report.improvements.map((s, i) => (
            <p key={i} className="na-block-item">
              <span className="na-block-num">{i + 1}</span>
              {s}
            </p>
          ))}
        </div>
      )}

      {/* CONCERNS */}
      {report.concerns && (
        <div className="na-block na-block-concern">
          <h3 className="na-block-heading na-h-red">Something to note</h3>
          <p className="na-block-body">{report.concerns}</p>
        </div>
      )}

      {/* FOOTER */}
      <div className="na-meta-row">
        {report.emotionalSignal && (
          <span className="na-tone">Tone: <strong>{report.emotionalSignal}</strong></span>
        )}
        <span className="na-date">{fmt(analysis.analyzedAt)}</span>
      </div>
    </div>
  );
};

/* ── Main ───────────────────────────────────────────────────── */

const NodeAnalysis = () => {
  const { id }   = useParams();
  const navigate  = useNavigate();

  const [runState,   setRunState]   = useState("idle");
  const [result,     setResult]     = useState(null);
  const [errorMsg,   setErrorMsg]   = useState("");
  const [lockedMsg,  setLockedMsg]  = useState("");
  const [history,    setHistory]    = useState([]);
  const [histLoaded, setHistLoaded] = useState(false);
  const [openIdx,    setOpenIdx]    = useState(null);

  useEffect(() => {
    getNodeAnalyses(id)
      .then((d) => { setHistory(d); setHistLoaded(true); })
      .catch(() => setHistLoaded(true));
  }, [id]);

  const handleRun = async () => {
    setRunState("loading");
    setErrorMsg("");
    setLockedMsg("");
    try {
      const data = await runNodeAnalysis(id);
      setResult(data);
      setRunState("done");
      setHistory((prev) => [data.analysis, ...prev]);
    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.message || "Something went wrong.";
      if (status === 429) {
        setLockedMsg(msg);
        setRunState("locked");
      } else {
        setErrorMsg(msg);
        setRunState("error");
      }
    }
  };

  const past = runState === "done" ? history.slice(1) : history;

  return (
    <div className="na-page">

      <header className="na-top">
        <button className="na-back" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back" /> Back
        </button>
        <div className="na-top-mid">
          <span className="na-top-badge">Dmyor Assist</span>
          <span className="na-top-title">Journey Analysis</span>
        </div>
        <div className="na-top-r" />
      </header>

      <main className="na-main">

        {runState === "idle" && (
          <section className="na-idle">
            <h1 className="na-idle-h">Analyse your journey</h1>
            <p className="na-idle-p">
              Scored across consistency, depth, progress, focus, and recency.
              One honest report.
            </p>
            <button className="na-cta" onClick={handleRun}>Run Analysis</button>
            <span className="na-idle-note">Once every 7 days per node</span>
          </section>
        )}

        {runState === "loading" && <TipLoader />}

        {runState === "locked" && (
          <div className="na-locked">
            <div className="na-locked-box">
              <i className="bx bx-time-five na-locked-icon" />
              <p className="na-locked-msg">{lockedMsg}</p>
              <p className="na-locked-sub">
                Come back once you've had more time with this node.
                Analysis is most useful when there's enough to reflect on.
              </p>
            </div>
            {histLoaded && history.length > 0 && (
              <p className="na-locked-hist-hint">You can still view past reports below.</p>
            )}
          </div>
        )}

        {runState === "error" && (
          <div className="na-error">
            <p>{errorMsg}</p>
            <button className="na-cta-secondary" onClick={handleRun}>Try again</button>
          </div>
        )}

        {runState === "done" && result?.analysis && (
          <Report
            analysis={result.analysis}
            deltas={result.deltas}
            previousAnalyzedAt={result.previousAnalyzedAt}
          />
        )}

        {histLoaded && past.length > 0 && (
          <section className="na-hist">
            <h2 className="na-hist-title">
              {runState === "done" ? "Previous reports" : "Past reports"}
            </h2>
            <div className="na-hist-list">
              {past.map((a, i) => {
                const open = openIdx === i;
                const c    = a.scores?.composite ?? null;
                return (
                  <div key={a._id} className={`na-hist-card ${open ? "open" : ""}`}>
                    <button className="na-hist-toggle" onClick={() => setOpenIdx(open ? null : i)}>
                      <div className="na-hist-left">
                        <div className="na-hist-score-block">
                          <span className="na-hist-score" style={{ color: color(c) }}>{c ?? "—"}</span>
                          <span className="na-hist-score-of">/100</span>
                        </div>
                        <div className="na-hist-info">
                          <span className="na-hist-date">{fmt(a.analyzedAt)}</span>
                        </div>
                      </div>
                      <div className="na-hist-right">
                        <i className="bx bx-chevron-down na-hist-chevron" />
                      </div>
                    </button>
                    {open && (
                      <div className="na-hist-body">
                        <Report analysis={a} isHistory />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {histLoaded && past.length === 0 && runState === "idle" && (
          <p className="na-empty">No previous reports yet.</p>
        )}

      </main>
    </div>
  );
};

export default NodeAnalysis;
